import RechargeCooldownLabel from "../../recharge/RechargeCooldownLabel";
import React, { useState, useEffect, useRef } from "react";
import { useTranslationPlus } from "@/Core/i18n/useTranslationPlus";
import PopupMask from "@/componentsUI/PopupMask";
import ButtonUI from "@/componentsUI/Button";
import styles from "./index.module.less";

export interface MexicoConfirmModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void;
  type: "deposit" | "withdraw";
  loading?: boolean;
  disabled?: boolean;
  autoTriggerSeconds?: number; // 自动触发秒数，默认5秒
}

const MexicoConfirmModal: React.FC<MexicoConfirmModalProps> = ({
  visible,
  onClose,
  onConfirm,
  type,
  loading = false,
  disabled = false,
  autoTriggerSeconds = 5,
}) => {
  const { t } = useTranslationPlus();
  const [countdown, setCountdown] = useState(autoTriggerSeconds);
  const [isAutoTriggered, setIsAutoTriggered] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 清理定时器
  const clearTimers = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  };

  // 启动倒计时和自动触发
  const startAutoTrigger = () => {
    if (disabled) return;
    setCountdown(autoTriggerSeconds);

    // 倒计时显示
    countdownTimerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // 自动触发
    timerRef.current = setTimeout(() => {
      if (!isAutoTriggered) {
        setIsAutoTriggered(true);
        onConfirm();
      }
    }, autoTriggerSeconds * 1000);
  };

  // 监听弹窗显示状态
  useEffect(() => {
    if (visible) {
      setIsAutoTriggered(false);
      startAutoTrigger();
    } else {
      clearTimers();
      setIsAutoTriggered(false);
      setCountdown(autoTriggerSeconds);
    }

    return () => {
      clearTimers();
    };
  }, [visible, disabled]);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, []);

  const handleClose = () => {
    clearTimers();
    setIsAutoTriggered(false);
    setCountdown(autoTriggerSeconds);
    onClose();
  };

  const handleConfirm = () => {
    // 如果已经自动触发过，则不再处理手动点击
    if (disabled || loading || isAutoTriggered) {
      return;
    }

    clearTimers(); // 清除定时器，防止重复触发
    setIsAutoTriggered(true);
    onConfirm();
  };

  // 确认按钮是否应该禁用
  const isConfirmDisabled = disabled || loading || isAutoTriggered;

  // 确认按钮文本
  const getConfirmButtonText = () => {
    if (loading) {
      return t("Wallet.Processing");
    }
    if (isAutoTriggered) {
      return t("Wallet.Submitted");
    }
    if (countdown > 0) {
      return `${t("Wallet.Confirm")} (${countdown}s)`;
    }
    return t("Wallet.Confirm");
  };

  return (
    <PopupMask visible={visible} onClose={handleClose}>
      <div className={styles.mexicoConfirmModal}>
        <div className={styles.modalHeader}>
          <div className={styles.titleText}>
            {type === "deposit"
              ? t("Wallet.Deposit Confirmation")
              : t("Wallet.Withdraw Confirmation")}
          </div>
          <button className={styles.closeBtn} onClick={handleClose}>
            ×
          </button>
        </div>

        <div className={styles.modalContent}>
          <div className={styles.tipBox}>
            <div className={styles.tipTitle}>
              💡 {t("Wallet.Important Tip")}
            </div>
            <div className={styles.tipContent}>
              Tip: En la interfaz de depósito y retiro, el campo Concept solo
              puede completarse con <strong>pago</strong>, <strong>pay</strong>{" "}
              o <strong>payout</strong> para mejorar la tasa de éxito de los
              depósitos y retiros.
            </div>
          </div>

          <div className={styles.confirmMessage}>
            {type === "deposit"
              ? t("Wallet.Please confirm your deposit request")
              : t("Wallet.Please confirm your withdraw request")}
          </div>
        </div>

        <div className={styles.modalFooter}>
          <ButtonUI
            className={styles.cancelBtn}
            onClick={handleClose}
            disabled={loading || isAutoTriggered}
          >
            {t("Wallet.Cancel")}
          </ButtonUI>
          <ButtonUI
            className={styles.confirmBtn}
            type="primary"
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
            loading={loading}
          >
            {type === "deposit" ? <RechargeCooldownLabel cooldownLabel={t(loading ? "Wallet.Processing" : "Wallet.Confirm")}>{getConfirmButtonText()}</RechargeCooldownLabel> : getConfirmButtonText()}
          </ButtonUI>
        </div>
      </div>
    </PopupMask>
  );
};

export default MexicoConfirmModal;
